import React, { useState, createContext, useContext, Component } from 'react';
// ─── Theme Token Types ────────────────────────────────────────────────────────
interface Theme {
  pageBg: string;
  sidebarBg: string;
  surface: string;
  card: string;
  deepCard: string;
  inputBg: string;
  green: string;
  greenDim: string;
  greenFaint: string;
  greenGhost: string;
  white: string;
  textSecondary: string;
  textMuted: string;
  textLabel: string;
  border: string;
  borderCard: string;
  warning: string;
  error: string;
}
const DARK_THEME: Theme = {
  pageBg: '#111111',
  sidebarBg: '#0a0a0a',
  surface: '#1a1a1a',
  card: '#1f1f1f',
  deepCard: '#0f0f0f',
  inputBg: '#161616',
  green: '#1A8FE8',
  greenDim: '#1578C5',
  greenFaint: 'rgba(26,143,232,0.06)',
  greenGhost: 'rgba(26,143,232,0.12)',
  white: '#f0f0f0',
  textSecondary: '#5a5a5a',
  textMuted: '#3a3a3a',
  textLabel: '#4a4a4a',
  border: '#252525',
  borderCard: '#1f1f1f',
  warning: '#f0a030',
  error: '#e05252'
};
const LIGHT_THEME: Theme = {
  pageBg: '#f4f4f4',
  sidebarBg: '#ffffff',
  surface: '#eeeeee',
  card: '#e8e8e8',
  deepCard: '#f9f9f9',
  inputBg: '#ffffff',
  green: '#1A8FE8',
  greenDim: '#1578C5',
  greenFaint: 'rgba(26,143,232,0.08)',
  greenGhost: 'rgba(26,143,232,0.15)',
  white: '#1a1a1a',
  textSecondary: '#555555',
  textMuted: '#999999',
  textLabel: '#666666',
  border: '#d4d4d4',
  borderCard: '#e0e0e0',
  warning: '#c47a10',
  error: '#c04040'
};
const ThemeContext = createContext<Theme>(DARK_THEME);
// ─── Step Definitions ─────────────────────────────────────────────────────────
const STEPS = [{
  id: 1,
  label: 'Conditions',
  short: 'Conditions'
}, {
  id: 2,
  label: 'Borrower',
  short: 'Borrower'
}, {
  id: 3,
  label: 'Property',
  short: 'Property'
}, {
  id: 4,
  label: 'Terms',
  short: 'Terms'
}, {
  id: 5,
  label: 'Lender',
  short: 'Lender'
}];
// ─── Conditions Data ──────────────────────────────────────────────────────────
type DocStatus = 'uploaded' | 'pending' | 'na';
interface Condition {
  id: string;
  name: string;
  required: boolean;
  status: DocStatus;
}
const INITIAL_CONDITIONS: Condition[] = [{
  id: '1',
  name: 'Signed 1003 Loan Application',
  required: true,
  status: 'uploaded'
}, {
  id: '2',
  name: 'Credit Report Authorization',
  required: true,
  status: 'uploaded'
}, {
  id: '3',
  name: 'Pay Stubs — Most Recent 30 Days',
  required: true,
  status: 'uploaded'
}, {
  id: '4',
  name: 'W-2s — 2 Years',
  required: true,
  status: 'uploaded'
}, {
  id: '5',
  name: 'Federal Tax Returns — 2 Years',
  required: true,
  status: 'uploaded'
}, {
  id: '6',
  name: 'Bank Statements — 2 Months',
  required: true,
  status: 'uploaded'
}, {
  id: '7',
  name: 'Asset / Investment Statements',
  required: true,
  status: 'uploaded'
}, {
  id: '8',
  name: 'Purchase Agreement / Sales Contract',
  required: true,
  status: 'uploaded'
}, {
  id: '9',
  name: 'Appraisal Report',
  required: true,
  status: 'uploaded'
}, {
  id: '10',
  name: 'Title Commitment',
  required: true,
  status: 'uploaded'
}, {
  id: '11',
  name: 'Homeowners Insurance Binder',
  required: true,
  status: 'uploaded'
}, {
  id: '12',
  name: 'Government-Issued Photo ID',
  required: true,
  status: 'uploaded'
}, {
  id: '13',
  name: 'Employment Verification (VOE)',
  required: true,
  status: 'uploaded'
}, {
  id: '14',
  name: 'AUS Findings (DU / LP)',
  required: true,
  status: 'uploaded'
}];
// ─── Form State ───────────────────────────────────────────────────────────────
interface FormData {
  loName: string;
  loNmls: string;
  loEmail: string;
  loPhone: string;
  loSpecialist: string;
  loBranch: string;
  borrowerFirst: string;
  borrowerLast: string;
  coBorrowerFirst: string;
  coBorrowerLast: string;
  borrowerDob: string;
  propAddress: string;
  propCity: string;
  propState: string;
  propZip: string;
  propType: string;
  occupancy: string;
  appraisedValue: string;
  salesPrice: string;
  loanAmount: string;
  loanType: string;
  loanPurpose: string;
  interestRate: string;
  loanTerm: string;
  ltv: string;
  cltv: string;
  dti: string;
  downPayment: string;
  closingDate: string;
  escrowCompany: string;
  lockExpiration: string;
  lenderName: string;
  productName: string;
  ausFinding: string;
  specialInstructions: string;
  submissionNotes: string;
}
const EMPTY_FORM: FormData = {
  loName: '',
  loNmls: '',
  loEmail: '',
  loPhone: '',
  loSpecialist: '',
  loBranch: '',
  borrowerFirst: '',
  borrowerLast: '',
  coBorrowerFirst: '',
  coBorrowerLast: '',
  borrowerDob: '',
  propAddress: '',
  propCity: '',
  propState: '',
  propZip: '',
  propType: '',
  occupancy: '',
  appraisedValue: '',
  salesPrice: '',
  loanAmount: '',
  loanType: '',
  loanPurpose: '',
  interestRate: '',
  loanTerm: '',
  ltv: '',
  cltv: '',
  dti: '',
  downPayment: '',
  closingDate: '',
  escrowCompany: '',
  lockExpiration: '',
  lenderName: '',
  productName: '',
  ausFinding: '',
  specialInstructions: '',
  submissionNotes: ''
};
// ─── Shared Field Components ──────────────────────────────────────────────────
function Field({
  label,
  value,
  onChange,
  type = 'text',
  placeholder = '',
  required = false,
  prefix = '',
  suffix = '',
  hint = '',
  fullWidth = false
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  required?: boolean;
  prefix?: string;
  suffix?: string;
  hint?: string;
  fullWidth?: boolean;
}) {
  const c = useContext(ThemeContext);
  const [focused, setFocused] = useState(false);
  return <div style={{
    display: 'flex',
    flexDirection: 'column',
    gap: '7px',
    gridColumn: fullWidth ? '1 / -1' : undefined
  }} data-id="element-1">
      <label style={{
      fontSize: '10px',
      fontWeight: 700,
      letterSpacing: '1.5px',
      textTransform: 'uppercase',
      color: focused ? c.green : c.textLabel,
      transition: 'color 0.15s'
    }} data-id="element-2">
        {label}
        {required && <span style={{
        color: c.green,
        marginLeft: '3px'
      }} data-id="element-3">
            *
          </span>}
      </label>
      <div style={{
      position: 'relative',
      display: 'flex',
      alignItems: 'center'
    }} data-id="element-4">
        {prefix && <span style={{
        position: 'absolute',
        left: '14px',
        color: c.textSecondary,
        fontSize: '14px',
        pointerEvents: 'none',
        zIndex: 1
      }} data-id="element-5">
            {prefix}
          </span>}
        <input type={type} value={value} onChange={e => onChange(e.target.value)} onFocus={() => setFocused(true)} onBlur={() => setFocused(false)} placeholder={placeholder} style={{
        width: '100%',
        backgroundColor: c.inputBg,
        border: `1px solid ${focused ? c.green : c.border}`,
        borderRadius: '6px',
        padding: `13px ${suffix ? '42px' : '16px'} 13px ${prefix ? '32px' : '16px'}`,
        fontSize: '14px',
        color: c.white,
        outline: 'none',
        boxShadow: focused ? `0 0 0 1px ${c.green}` : 'none',
        transition: 'border-color 0.15s, box-shadow 0.15s',
        boxSizing: 'border-box',
        fontVariantNumeric: 'tabular-nums'
      }} data-id="element-6" />
        {suffix && <span style={{
        position: 'absolute',
        right: '14px',
        color: c.textSecondary,
        fontSize: '12px',
        pointerEvents: 'none',
        fontWeight: 600,
        letterSpacing: '0.5px'
      }} data-id="element-7">
            {suffix}
          </span>}
      </div>
      {hint && <span style={{
      fontSize: '10px',
      color: c.textMuted,
      letterSpacing: '0.5px'
    }} data-id="element-8">
          {hint}
        </span>}
    </div>;
}
function Select({
  label,
  value,
  onChange,
  options,
  required = false,
  fullWidth = false
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: {
    value: string;
    label: string;
  }[];
  required?: boolean;
  fullWidth?: boolean;
}) {
  const c = useContext(ThemeContext);
  const [focused, setFocused] = useState(false);
  return <div style={{
    display: 'flex',
    flexDirection: 'column',
    gap: '7px',
    gridColumn: fullWidth ? '1 / -1' : undefined
  }} data-id="element-9">
      <label style={{
      fontSize: '10px',
      fontWeight: 700,
      letterSpacing: '1.5px',
      textTransform: 'uppercase',
      color: focused ? c.green : c.textLabel,
      transition: 'color 0.15s'
    }} data-id="element-10">
        {label}
        {required && <span style={{
        color: c.green,
        marginLeft: '3px'
      }} data-id="element-11">
            *
          </span>}
      </label>
      <div style={{
      position: 'relative'
    }} data-id="element-12">
        <select value={value} onChange={e => onChange(e.target.value)} onFocus={() => setFocused(true)} onBlur={() => setFocused(false)} style={{
        width: '100%',
        backgroundColor: c.inputBg,
        border: `1px solid ${focused ? c.green : c.border}`,
        borderRadius: '6px',
        padding: '13px 40px 13px 16px',
        fontSize: '14px',
        color: value ? c.white : c.textSecondary,
        outline: 'none',
        appearance: 'none',
        cursor: 'pointer',
        boxShadow: focused ? `0 0 0 1px ${c.green}` : 'none',
        transition: 'border-color 0.15s, box-shadow 0.15s',
        boxSizing: 'border-box'
      }} data-id="element-13">
          <option value="" style={{
          color: c.textSecondary
        }} data-id="element-14">
            Select…
          </option>
          {options.map(o => <option key={o.value} value={o.value} style={{
          backgroundColor: c.inputBg,
          color: c.white
        }} data-id="element-15">
              {o.label}
            </option>)}
        </select>
        <span style={{
        position: 'absolute',
        right: '14px',
        top: '50%',
        transform: 'translateY(-50%)',
        color: c.textSecondary,
        fontSize: '9px',
        pointerEvents: 'none',
        fontWeight: 700
      }} data-id="element-16">
          ▼
        </span>
      </div>
    </div>;
}
function Textarea({
  label,
  value,
  onChange,
  placeholder = '',
  rows = 4,
  fullWidth = false
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  rows?: number;
  fullWidth?: boolean;
}) {
  const c = useContext(ThemeContext);
  const [focused, setFocused] = useState(false);
  return <div style={{
    display: 'flex',
    flexDirection: 'column',
    gap: '7px',
    gridColumn: fullWidth ? '1 / -1' : undefined
  }} data-id="element-17">
      <label style={{
      fontSize: '10px',
      fontWeight: 700,
      letterSpacing: '1.5px',
      textTransform: 'uppercase',
      color: focused ? c.green : c.textLabel,
      transition: 'color 0.15s'
    }} data-id="element-18">
        {label}
      </label>
      <textarea value={value} onChange={e => onChange(e.target.value)} onFocus={() => setFocused(true)} onBlur={() => setFocused(false)} placeholder={placeholder} rows={rows} style={{
      width: '100%',
      backgroundColor: c.inputBg,
      border: `1px solid ${focused ? c.green : c.border}`,
      borderRadius: '6px',
      padding: '13px 16px',
      fontSize: '14px',
      color: c.white,
      outline: 'none',
      resize: 'vertical',
      fontFamily: 'inherit',
      boxShadow: focused ? `0 0 0 1px ${c.green}` : 'none',
      transition: 'border-color 0.15s, box-shadow 0.15s',
      boxSizing: 'border-box'
    }} data-id="element-19" />
    </div>;
}
function Checkbox({
  label,
  checked,
  onChange,
  fullWidth = false
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  fullWidth?: boolean;
}) {
  const c = useContext(ThemeContext);
  return <label style={{
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    cursor: 'pointer',
    gridColumn: fullWidth ? '1 / -1' : undefined,
    userSelect: 'none'
  }} data-id="element-20">
      <div style={{
      width: '18px',
      height: '18px',
      borderRadius: '4px',
      border: `1px solid ${checked ? c.green : c.border}`,
      backgroundColor: checked ? c.green : c.inputBg,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.15s',
      flexShrink: 0
    }} data-id="element-21">
        {checked && <span style={{
        color: '#0a0a0a',
        fontSize: '12px',
        fontWeight: 900
      }} data-id="element-22">
            ✓
          </span>}
      </div>
      <span style={{
      fontSize: '13px',
      color: c.white
    }} data-id="element-23">
        {label}
      </span>
    </label>;
}
function YesNoToggle({
  label,
  value,
  onChange,
  fullWidth = false
}: {
  label: string;
  value: boolean;
  onChange: (v: boolean) => void;
  fullWidth?: boolean;
}) {
  const c = useContext(ThemeContext);
  return <div style={{
    display: 'flex',
    flexDirection: 'column',
    gap: '7px',
    gridColumn: fullWidth ? '1 / -1' : undefined
  }} data-id="element-24">
      <label style={{
      fontSize: '10px',
      fontWeight: 700,
      letterSpacing: '1.5px',
      textTransform: 'uppercase',
      color: c.textLabel
    }} data-id="element-25">
        {label}
      </label>
      <div style={{
      display: 'flex',
      gap: '8px'
    }} data-id="element-26">
        <button onClick={() => onChange(true)} style={{
        flex: 1,
        padding: '10px',
        borderRadius: '6px',
        border: `1px solid ${value === true ? c.green : c.border}`,
        backgroundColor: value === true ? c.greenFaint : c.inputBg,
        color: value === true ? c.green : c.textSecondary,
        fontSize: '13px',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.15s'
      }} data-id="element-27">
          Yes
        </button>
        <button onClick={() => onChange(false)} style={{
        flex: 1,
        padding: '10px',
        borderRadius: '6px',
        border: `1px solid ${value === false ? c.warning : c.border}`,
        backgroundColor: value === false ? `${c.warning}15` : c.inputBg,
        color: value === false ? c.warning : c.textSecondary,
        fontSize: '13px',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.15s'
      }} data-id="element-28">
          No
        </button>
      </div>
    </div>;
}
function SectionDivider({
  label
}: {
  label: string;
}) {
  const c = useContext(ThemeContext);
  return <div style={{
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    margin: '36px 0 24px'
  }} data-id="element-29">
      <div style={{
      flex: 1,
      height: '1px',
      backgroundColor: c.borderCard
    }} data-id="element-30" />
      <span style={{
      fontSize: '9px',
      fontWeight: 700,
      letterSpacing: '2px',
      textTransform: 'uppercase',
      color: c.green
    }} data-id="element-31">
        {label}
      </span>
      <div style={{
      flex: 1,
      height: '1px',
      backgroundColor: c.borderCard
    }} data-id="element-32" />
    </div>;
}
function FieldSection({
  children
}: {
  children: React.ReactNode;
}) {
  const c = useContext(ThemeContext);
  return <div style={{
    borderLeft: `3px solid ${c.green}`,
    paddingLeft: '28px'
  }} data-id="element-33">
      {children}
    </div>;
}
// ─── Step Content Components ──────────────────────────────────────────────────
function StepLoanOfficer({
  form,
  set
}: {
  form: FormData;
  set: (k: keyof FormData, v: any) => void;
}) {
  return <FieldSection data-id="element-34">
      <div style={{
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '20px'
    }} data-id="element-35">
        <Field label="Full Name" value={form.loName} onChange={v => set('loName', v)} required placeholder="Jane Smith" data-id="element-36" />
        <Field label="NMLS ID" value={form.loNmls} onChange={v => set('loNmls', v)} required placeholder="1234567" data-id="element-37" />
        <Field label="Email Address" value={form.loEmail} onChange={v => set('loEmail', v)} type="email" required placeholder="jane@ezloansmi.com" data-id="element-38" />
        <Field label="Phone Number" value={form.loPhone} onChange={v => set('loPhone', v)} type="tel" placeholder="(844) 204-7863" data-id="element-39" />
        <Field label="Branch" value={form.loBranch} onChange={v => set('loBranch', v)} placeholder="Troy, MI" data-id="element-40" />
        <Select label="Submission Specialist" value={form.loSpecialist} onChange={v => set('loSpecialist', v)} required options={[{
        value: 'sarah',
        label: 'Sarah Johnson'
      }, {
        value: 'mike',
        label: 'Mike Chen'
      }, {
        value: 'lisa',
        label: 'Lisa Rodriguez'
      }, {
        value: 'david',
        label: 'David Kim'
      }, {
        value: 'amanda',
        label: 'Amanda Torres'
      }]} data-id="element-41" />
      </div>
    </FieldSection>;
}
function StepBorrower({
  form,
  set
}: {
  form: FormData;
  set: (k: keyof FormData, v: any) => void;
}) {
  return <FieldSection data-id="element-42">
      <div style={{
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '20px'
    }} data-id="element-43">
        <Field label="First Name" value={form.borrowerFirst} onChange={v => set('borrowerFirst', v)} required placeholder="John" data-id="element-44" />
        <Field label="Last Name" value={form.borrowerLast} onChange={v => set('borrowerLast', v)} required placeholder="Doe" data-id="element-45" />
        <Field label="Date of Birth" value={form.borrowerDob} onChange={v => set('borrowerDob', v)} type="date" required data-id="element-46" />
      </div>
      <SectionDivider label="Co-Borrower" data-id="element-47" />
      <div style={{
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '20px'
    }} data-id="element-48">
        <Field label="First Name" value={form.coBorrowerFirst} onChange={v => set('coBorrowerFirst', v)} placeholder="Jane" data-id="element-49" />
        <Field label="Last Name" value={form.coBorrowerLast} onChange={v => set('coBorrowerLast', v)} placeholder="Doe" data-id="element-50" />
      </div>
    </FieldSection>;
}
function StepProperty({
  form,
  set
}: {
  form: FormData;
  set: (k: keyof FormData, v: any) => void;
}) {
  const states = ['AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'];
  return <FieldSection data-id="element-51">
      <div style={{
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '20px'
    }} data-id="element-52">
        <Field label="Street Address" value={form.propAddress} onChange={v => set('propAddress', v)} required placeholder="123 Main Street" fullWidth data-id="element-53" />
        <Field label="City" value={form.propCity} onChange={v => set('propCity', v)} required placeholder="Troy" data-id="element-54" />
        <Select label="State" value={form.propState} onChange={v => set('propState', v)} required options={states.map(s => ({
        value: s,
        label: s
      }))} data-id="element-55" />
        <Field label="ZIP Code" value={form.propZip} onChange={v => set('propZip', v)} required placeholder="48083" data-id="element-56" />
        <Select label="Property Type" value={form.propType} onChange={v => set('propType', v)} required options={[{
        value: 'sfr',
        label: 'Single Family Residence'
      }, {
        value: 'condo',
        label: 'Condominium'
      }, {
        value: 'townhome',
        label: 'Townhome / PUD'
      }, {
        value: '2unit',
        label: '2-Unit'
      }, {
        value: '3unit',
        label: '3-Unit'
      }, {
        value: '4unit',
        label: '4-Unit'
      }, {
        value: 'mfh',
        label: 'Manufactured Home'
      }]} data-id="element-57" />
        <Select label="Occupancy" value={form.occupancy} onChange={v => set('occupancy', v)} required options={[{
        value: 'primary',
        label: 'Primary Residence'
      }, {
        value: 'second',
        label: 'Second Home'
      }, {
        value: 'investment',
        label: 'Investment Property'
      }]} data-id="element-58" />
        <Field label="Appraised Value" value={form.appraisedValue} onChange={v => set('appraisedValue', v)} required prefix="$" placeholder="350,000" data-id="element-59" />
        <Field label="Sales Price" value={form.salesPrice} onChange={v => set('salesPrice', v)} required prefix="$" placeholder="345,000" data-id="element-60" />
      </div>
    </FieldSection>;
}
function StepLoanTerms({
  form,
  set
}: {
  form: FormData;
  set: (k: keyof FormData, v: any) => void;
}) {
  return <FieldSection data-id="element-61">
      <div style={{
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '20px'
    }} data-id="element-62">
        <Field label="Loan Amount" value={form.loanAmount} onChange={v => set('loanAmount', v)} required prefix="$" placeholder="310,000" data-id="element-63" />
        <Select label="Loan Type" value={form.loanType} onChange={v => set('loanType', v)} required options={[{
        value: 'conv',
        label: 'Conventional'
      }, {
        value: 'fha',
        label: 'FHA'
      }, {
        value: 'va',
        label: 'VA'
      }, {
        value: 'usda',
        label: 'USDA'
      }, {
        value: 'jumbo',
        label: 'Jumbo'
      }, {
        value: 'nonqm',
        label: 'Non-QM'
      }]} data-id="element-64" />
        <Select label="Loan Purpose" value={form.loanPurpose} onChange={v => set('loanPurpose', v)} required options={[{
        value: 'purchase',
        label: 'Purchase'
      }, {
        value: 'rr',
        label: 'Rate & Term Refinance'
      }, {
        value: 'cashout',
        label: 'Cash-Out Refinance'
      }, {
        value: 'streamline',
        label: 'Streamline Refinance'
      }]} data-id="element-65" />
        <Select label="Loan Term" value={form.loanTerm} onChange={v => set('loanTerm', v)} required options={[{
        value: '360',
        label: '30-Year Fixed'
      }, {
        value: '240',
        label: '20-Year Fixed'
      }, {
        value: '180',
        label: '15-Year Fixed'
      }, {
        value: '120',
        label: '10-Year Fixed'
      }, {
        value: '5_1arm',
        label: '5/1 ARM'
      }, {
        value: '7_1arm',
        label: '7/1 ARM'
      }, {
        value: '10_1arm',
        label: '10/1 ARM'
      }]} data-id="element-66" />
        <Field label="Interest Rate" value={form.interestRate} onChange={v => set('interestRate', v)} required suffix="%" placeholder="6.875" hint="Note rate, not APR" data-id="element-67" />
        <Field label="LTV" value={form.ltv} onChange={v => set('ltv', v)} required suffix="%" placeholder="88.57" data-id="element-68" />
        <Field label="CLTV" value={form.cltv} onChange={v => set('cltv', v)} suffix="%" placeholder="88.57" data-id="element-69" />
        <Field label="Back-End DTI" value={form.dti} onChange={v => set('dti', v)} required suffix="%" placeholder="43.5" data-id="element-70" />
      </div>
    </FieldSection>;
}
function StepTransaction({
  form,
  set
}: {
  form: FormData;
  set: (k: keyof FormData, v: any) => void;
}) {
  return <FieldSection data-id="element-71">
      <div style={{
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '20px'
    }} data-id="element-72">
        <Field label="Sales Price" value={form.salesPrice} onChange={v => set('salesPrice', v)} required prefix="$" placeholder="345,000" data-id="element-73" />
        <Field label="Down Payment" value={form.downPayment} onChange={v => set('downPayment', v)} required prefix="$" placeholder="35,000" data-id="element-74" />
        <Field label="Target Closing Date" value={form.closingDate} onChange={v => set('closingDate', v)} type="date" required data-id="element-75" />
        <Field label="Lock Expiration Date" value={form.lockExpiration} onChange={v => set('lockExpiration', v)} type="date" data-id="element-76" />
        <Field label="Escrow / Title Company" value={form.escrowCompany} onChange={v => set('escrowCompany', v)} placeholder="First American Title" fullWidth data-id="element-77" />
      </div>
    </FieldSection>;
}
function StepLender({
  form,
  set
}: {
  form: FormData;
  set: (k: keyof FormData, v: any) => void;
}) {
  return <FieldSection data-id="element-78">
      <div style={{
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '20px'
    }} data-id="element-79">
        <Field label="Lender / Investor" value={form.lenderName} onChange={v => set('lenderName', v)} required placeholder="United Wholesale Mortgage" data-id="element-80" />
        <Field label="Product / Program" value={form.productName} onChange={v => set('productName', v)} required placeholder="Conv 30yr Fixed" data-id="element-81" />
        <Select label="AUS Finding" value={form.ausFinding} onChange={v => set('ausFinding', v)} required options={[{
        value: 'approve_eligible',
        label: 'Approve / Eligible (DU)'
      }, {
        value: 'accept',
        label: 'Accept (LP)'
      }, {
        value: 'refer_eligible',
        label: 'Refer / Eligible'
      }, {
        value: 'refer_caution',
        label: 'Refer with Caution'
      }, {
        value: 'manual',
        label: 'Manual Underwrite'
      }]} data-id="element-82" />
        <Field label="Rate Lock Expiration" value={form.lockExpiration} onChange={v => set('lockExpiration', v)} type="date" data-id="element-83" />
        <Textarea label="Special Instructions" value={form.specialInstructions} onChange={v => set('specialInstructions', v)} placeholder="Rush request, lender-specific notes, exceptions…" rows={3} fullWidth data-id="element-84" />
        <Textarea label="Submission Notes" value={form.submissionNotes} onChange={v => set('submissionNotes', v)} placeholder="Notes for the submission specialist…" rows={3} fullWidth data-id="element-85" />
      </div>
    </FieldSection>;
}
// ─── Condition Row ────────────────────────────────────────────────────────────
function ConditionRow({
  condition
}: {
  condition: Condition;
}) {
  const c = useContext(ThemeContext);
  const isUploaded = condition.status === 'uploaded';
  return <div style={{
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    padding: '12px 16px 12px 0',
    borderRadius: '4px',
    border: `1px solid ${isUploaded ? `${c.green}33` : `${c.warning}33`}`,
    backgroundColor: isUploaded ? c.greenFaint : c.deepCard,
    overflow: 'hidden',
    position: 'relative',
    marginBottom: '6px'
  }} data-id="element-86">
      <div style={{
      width: '3px',
      alignSelf: 'stretch',
      backgroundColor: isUploaded ? c.green : c.warning,
      flexShrink: 0,
      borderRadius: '0 2px 2px 0'
    }} data-id="element-87" />
      <span style={{
      flex: 1,
      fontSize: '13px',
      color: c.white,
      letterSpacing: '0.1px'
    }} data-id="element-88">
        {condition.name}
      </span>
      <span style={{
      fontSize: '9px',
      fontWeight: 700,
      letterSpacing: '1.5px',
      textTransform: 'uppercase',
      padding: '3px 10px',
      borderRadius: '2px',
      flexShrink: 0,
      marginRight: '4px',
      backgroundColor: isUploaded ? `${c.green}20` : `${c.warning}20`,
      color: isUploaded ? c.green : c.warning,
      border: `1px solid ${isUploaded ? `${c.green}40` : `${c.warning}40`}`
    }} data-id="element-89">
        {isUploaded ? 'Uploaded' : 'Not Uploaded'}
      </span>
    </div>;
}
// ─── Conditions Step ──────────────────────────────────────────────────────────
function StepConditions({
  conditions,
  uploadedCount,
  requiredCount,
  allUploaded
}: {
  conditions: Condition[];
  uploadedCount: number;
  requiredCount: number;
  allUploaded: boolean;
}) {
  const c = useContext(ThemeContext);
  const pendingCount = requiredCount - uploadedCount;
  return <div style={{
    display: 'flex',
    flexDirection: 'column',
    gap: '28px'
  }} data-id="element-90">
      {/* Stats */}
      <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: '12px'
    }} data-id="element-91">
        <div style={{
        backgroundColor: c.deepCard,
        border: `1px solid ${c.borderCard}`,
        borderRadius: '4px',
        padding: '16px 18px'
      }} data-id="element-92">
          <div style={{
          fontSize: '28px',
          fontWeight: 800,
          color: c.green,
          fontVariantNumeric: 'tabular-nums',
          lineHeight: 1
        }} data-id="element-93">
            {uploadedCount}
          </div>
          <div style={{
          fontSize: '9px',
          fontWeight: 700,
          letterSpacing: '1.5px',
          textTransform: 'uppercase',
          color: c.textLabel,
          marginTop: '6px'
        }} data-id="element-94">
            Uploaded
          </div>
        </div>
        <div style={{
        backgroundColor: c.deepCard,
        border: `1px solid ${c.borderCard}`,
        borderRadius: '4px',
        padding: '16px 18px'
      }} data-id="element-95">
          <div style={{
          fontSize: '28px',
          fontWeight: 800,
          color: pendingCount > 0 ? c.warning : c.green,
          fontVariantNumeric: 'tabular-nums',
          lineHeight: 1
        }} data-id="element-96">
            {pendingCount}
          </div>
          <div style={{
          fontSize: '9px',
          fontWeight: 700,
          letterSpacing: '1.5px',
          textTransform: 'uppercase',
          color: c.textLabel,
          marginTop: '6px'
        }} data-id="element-97">
            Not Uploaded
          </div>
        </div>
        <div style={{
        backgroundColor: c.deepCard,
        border: `1px solid ${c.borderCard}`,
        borderRadius: '4px',
        padding: '16px 18px'
      }} data-id="element-98">
          <div style={{
          fontSize: '28px',
          fontWeight: 800,
          color: c.textSecondary,
          fontVariantNumeric: 'tabular-nums',
          lineHeight: 1
        }} data-id="element-99">
            {requiredCount}
          </div>
          <div style={{
          fontSize: '9px',
          fontWeight: 700,
          letterSpacing: '1.5px',
          textTransform: 'uppercase',
          color: c.textLabel,
          marginTop: '6px'
        }} data-id="element-100">
            Total Required
          </div>
        </div>
      </div>

      {/* Progress */}
      <div data-id="element-101">
        <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: '8px'
      }} data-id="element-102">
          <span style={{
          fontSize: '9px',
          fontWeight: 700,
          letterSpacing: '2px',
          textTransform: 'uppercase',
          color: c.textLabel
        }} data-id="element-103">
            Upload Progress
          </span>
          <span style={{
          fontSize: '11px',
          fontWeight: 800,
          color: allUploaded ? c.green : c.warning,
          fontVariantNumeric: 'tabular-nums'
        }} data-id="element-104">
            {Math.round(uploadedCount / requiredCount * 100)}%
          </span>
        </div>
        <div style={{
        height: '2px',
        backgroundColor: c.border,
        borderRadius: '1px',
        overflow: 'hidden'
      }} data-id="element-105">
          <div style={{
          height: '100%',
          width: `${uploadedCount / requiredCount * 100}%`,
          backgroundColor: allUploaded ? c.green : c.warning,
          transition: 'width 0.4s ease'
        }} data-id="element-106" />
        </div>
      </div>

      {/* Status banner */}
      {!allUploaded && <div style={{
      padding: '12px 16px',
      backgroundColor: `${c.warning}10`,
      border: `1px solid ${c.warning}33`,
      borderLeft: `3px solid ${c.warning}`,
      borderRadius: '0 4px 4px 0'
    }} data-id="element-107">
          <p style={{
        fontSize: '12px',
        fontWeight: 700,
        color: c.warning,
        margin: '0 0 3px'
      }} data-id="element-108">
            {pendingCount} document{pendingCount !== 1 ? 's' : ''} not yet
            uploaded
          </p>
          <p style={{
        fontSize: '11px',
        color: c.textSecondary,
        margin: 0,
        lineHeight: '1.5'
      }} data-id="element-109">
            All documents must be uploaded in the Document Management system
            before you can proceed.
          </p>
        </div>}
      {allUploaded && <div style={{
      padding: '12px 16px',
      backgroundColor: c.greenFaint,
      border: `1px solid ${c.green}33`,
      borderLeft: `3px solid ${c.green}`,
      borderRadius: '0 4px 4px 0'
    }} data-id="element-110">
          <p style={{
        fontSize: '12px',
        fontWeight: 700,
        color: c.green,
        margin: '0 0 3px'
      }} data-id="element-111">
            All documents uploaded
          </p>
          <p style={{
        fontSize: '11px',
        color: c.textSecondary,
        margin: 0
      }} data-id="element-112">
            You may proceed with the submission.
          </p>
        </div>}

      {/* Document list */}
      <div data-id="element-113">
        <div style={{
        fontSize: '9px',
        fontWeight: 700,
        letterSpacing: '2px',
        textTransform: 'uppercase',
        color: c.textLabel,
        marginBottom: '12px'
      }} data-id="element-114">
          Required Documents
        </div>
        {conditions.map(cond => <ConditionRow key={cond.id} condition={cond} data-id="element-115" />)}
      </div>
    </div>;
}
// ─── Main Component ───────────────────────────────────────────────────────────
export function LoanSubmissionForm() {
  const [isDark, setIsDark] = useState(false);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState<FormData>(EMPTY_FORM);
  const [conditions] = useState<Condition[]>(INITIAL_CONDITIONS);
  const [submitted, setSubmitted] = useState(false);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const c = isDark ? DARK_THEME : LIGHT_THEME;
  const setField = (key: keyof FormData, value: any) => setForm(prev => ({
    ...prev,
    [key]: value
  }));
  const requiredConditions = conditions.filter(cond => cond.required);
  const uploadedRequired = requiredConditions.filter(cond => cond.status === 'uploaded');
  const allUploaded = uploadedRequired.length === requiredConditions.length;
  const handleSubmit = () => {
    if (!allUploaded) return;
    setSubmitted(true);
  };
  const handleNext = () => {
    if (step < 5) {
      setCompletedSteps(prev => new Set([...prev, step]));
      setStep(s => s + 1);
    }
  };
  const handleBack = () => {
    if (step > 1) setStep(s => s - 1);
  };
  const goToStep = (n: number) => {
    if (n <= step || completedSteps.has(n - 1)) {
      if (step < n) setCompletedSteps(prev => new Set([...prev, step]));
      setStep(n);
    }
  };
  const stepTitles: Record<number, {
    title: string;
    subtitle: string;
  }> = {
    1: {
      title: 'Conditions',
      subtitle: 'All documents must be uploaded before proceeding'
    },
    2: {
      title: 'Borrower',
      subtitle: 'Primary and co-borrower details'
    },
    3: {
      title: 'Property',
      subtitle: 'Subject property details'
    },
    4: {
      title: 'Loan Terms',
      subtitle: 'Core loan parameters and ratios'
    },
    5: {
      title: 'Lender',
      subtitle: 'Product selection, transaction details and notes'
    }
  };
  const padStep = (n: number) => String(n).padStart(2, '0');
  return <ThemeContext.Provider value={c} data-id="element-116">
      <div style={{
      display: 'flex',
      height: '100vh',
      backgroundColor: c.pageBg,
      fontFamily: "'Inter', 'Segoe UI', system-ui, -apple-system, sans-serif",
      overflow: 'hidden',
      transition: 'background-color 0.2s'
    }} data-id="element-117">
        {/* ── SIDEBAR ────────────────────────────────────────────────────── */}
        <aside style={{
        width: '268px',
        flexShrink: 0,
        backgroundColor: c.sidebarBg,
        borderRight: `1px solid ${c.border}`,
        borderLeft: `5px solid ${c.green}`,
        display: 'flex',
        flexDirection: 'column',
        padding: '36px 0',
        overflowY: 'auto',
        transition: 'background-color 0.2s'
      }} data-id="element-118">
          {/* Logo */}
          <div style={{
          padding: '0 28px 24px',
          borderBottom: `1px solid ${c.border}`
        }} data-id="element-119">
            <img src="https://cdn.magicpatterns.com/uploads/5eWChBo5cdoaqwAgAonezW/2246b9849a45a841ad455c43908851563c99a0fa.png" alt="Mortgage One Funding" style={{
            width: '140px',
            height: 'auto',
            filter: isDark ? 'brightness(1.1)' : 'none',
            transition: 'filter 0.2s'
          }} data-id="element-120" />
            <p style={{
            fontSize: '11px',
            color: c.textLabel,
            marginTop: '10px',
            letterSpacing: '0.3px'
          }} data-id="element-121">
              Loan Submission System
            </p>
          </div>

          {/* Theme Toggle */}
          <div style={{
          padding: '16px 28px',
          borderBottom: `1px solid ${c.border}`
        }} data-id="element-122">
            <button onClick={() => setIsDark(d => !d)} style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            width: '100%',
            padding: '8px 14px',
            borderRadius: '20px',
            border: `1px solid ${c.border}`,
            backgroundColor: c.deepCard,
            cursor: 'pointer',
            transition: 'all 0.2s',
            fontSize: '11px',
            fontWeight: 600,
            color: c.textSecondary,
            letterSpacing: '0.3px'
          }} onMouseEnter={e => {
            e.currentTarget.style.borderColor = c.green;
            e.currentTarget.style.color = c.green;
          }} onMouseLeave={e => {
            e.currentTarget.style.borderColor = c.border;
            e.currentTarget.style.color = c.textSecondary;
          }} data-id="element-123">
              <span style={{
              fontSize: '13px'
            }} data-id="element-124">
                {isDark ? '☀️' : '🌙'}
              </span>
              <span data-id="element-125">{isDark ? 'Light Mode' : 'Dark Mode'}</span>
            </button>
          </div>

          {/* Step Navigator */}
          <nav style={{
          flex: 1,
          padding: '20px 0'
        }} data-id="element-126">
            {STEPS.map(s => {
            const isActive = step === s.id;
            const isDone = completedSteps.has(s.id);
            const isLocked = s.id > step && !completedSteps.has(s.id);
            return <button key={s.id} onClick={() => !isLocked && goToStep(s.id)} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '14px',
              width: '100%',
              padding: '13px 28px',
              border: 'none',
              borderLeft: `3px solid ${isActive ? c.green : 'transparent'}`,
              backgroundColor: isActive ? c.greenFaint : 'transparent',
              cursor: isLocked ? 'default' : 'pointer',
              transition: 'all 0.15s',
              textAlign: 'left',
              position: 'relative',
              overflow: 'hidden'
            }} data-id="element-127">
                  <span style={{
                position: 'absolute',
                right: '-8px',
                top: '50%',
                transform: 'translateY(-50%)',
                fontSize: '52px',
                fontWeight: 900,
                color: isActive ? c.greenGhost : isDark ? '#1a1a1a' : '#e8e8e8',
                pointerEvents: 'none',
                userSelect: 'none',
                zIndex: 0,
                lineHeight: 1,
                letterSpacing: '-2px',
                transition: 'color 0.2s'
              }} data-id="element-128">
                    {padStep(s.id)}
                  </span>
                  <div style={{
                width: '26px',
                height: '26px',
                borderRadius: '50%',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '11px',
                fontWeight: 800,
                backgroundColor: isDone ? c.green : 'transparent',
                border: `2px solid ${isDone ? c.green : isActive ? c.green : c.textMuted}`,
                color: isDone ? isDark ? '#0a0a0a' : '#ffffff' : isActive ? c.green : c.textMuted,
                transition: 'all 0.2s',
                zIndex: 1,
                position: 'relative'
              }} data-id="element-129">
                    {isDone ? '✓' : padStep(s.id)}
                  </div>
                  <div style={{
                zIndex: 1,
                position: 'relative'
              }} data-id="element-130">
                    <p style={{
                  margin: 0,
                  fontSize: '13px',
                  fontWeight: isActive ? 700 : 400,
                  color: isActive ? c.white : isDone ? c.textSecondary : c.textMuted,
                  letterSpacing: isActive ? '0.2px' : '0',
                  transition: 'color 0.15s'
                }} data-id="element-131">
                      {s.label}
                    </p>
                    {isActive && <p style={{
                  margin: '2px 0 0',
                  fontSize: '9px',
                  fontWeight: 700,
                  color: c.green,
                  letterSpacing: '1.5px',
                  textTransform: 'uppercase'
                }} data-id="element-132">
                        In Progress
                      </p>}
                  </div>
                </button>;
          })}
          </nav>

          {/* Document Progress */}
          <div style={{
          margin: '0 20px',
          padding: '16px',
          borderRadius: '4px',
          backgroundColor: c.deepCard,
          border: `1px solid ${c.borderCard}`
        }} data-id="element-133">
            <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '10px'
          }} data-id="element-134">
              <span style={{
              fontSize: '9px',
              fontWeight: 700,
              letterSpacing: '2px',
              textTransform: 'uppercase',
              color: c.textLabel
            }} data-id="element-135">
                Documents
              </span>
              <span style={{
              fontSize: '13px',
              fontWeight: 800,
              color: allUploaded ? c.green : c.warning,
              fontVariantNumeric: 'tabular-nums'
            }} data-id="element-136">
                {uploadedRequired.length}
                <span style={{
                color: c.textMuted,
                fontWeight: 400
              }} data-id="element-137">
                  /{requiredConditions.length}
                </span>
              </span>
            </div>
            <div style={{
            height: '2px',
            backgroundColor: c.border,
            borderRadius: '1px',
            overflow: 'hidden'
          }} data-id="element-138">
              <div style={{
              height: '100%',
              width: `${requiredConditions.length > 0 ? uploadedRequired.length / requiredConditions.length * 100 : 0}%`,
              backgroundColor: allUploaded ? c.green : c.warning,
              transition: 'width 0.4s ease'
            }} data-id="element-139" />
            </div>
            <p style={{
            fontSize: '9px',
            color: c.textMuted,
            marginTop: '8px',
            letterSpacing: '0.5px',
            textTransform: 'uppercase',
            fontWeight: 600
          }} data-id="element-140">
              {allUploaded ? 'Ready for submission' : 'Upload all docs to submit'}
            </p>
          </div>
        </aside>

        {/* ── CONTENT AREA ───────────────────────────────────────────────── */}
        <main style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        backgroundColor: c.pageBg,
        transition: 'background-color 0.2s'
      }} data-id="element-141">
          {/* Step Header */}
          <div style={{
          padding: '32px 56px 28px',
          borderBottom: `1px solid ${c.border}`,
          flexShrink: 0,
          position: 'relative',
          overflow: 'hidden'
        }} data-id="element-142">
            <span style={{
            position: 'absolute',
            right: '48px',
            top: '50%',
            transform: 'translateY(-50%)',
            fontSize: '140px',
            fontWeight: 900,
            color: isDark ? 'rgba(255,255,255,0.025)' : 'rgba(0,0,0,0.04)',
            letterSpacing: '-8px',
            pointerEvents: 'none',
            userSelect: 'none',
            zIndex: 0,
            lineHeight: 1
          }} data-id="element-143">
              {padStep(step)}
            </span>
            <div style={{
            position: 'relative',
            zIndex: 1,
            maxWidth: '860px',
            margin: '0 auto'
          }} data-id="element-144">
              <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              marginBottom: '10px'
            }} data-id="element-145">
                <span style={{
                fontSize: '11px',
                fontWeight: 700,
                letterSpacing: '3px',
                textTransform: 'uppercase',
                color: c.textLabel
              }} data-id="element-146">
                  Step {padStep(step)} / {padStep(STEPS.length)}
                </span>
                <div style={{
                display: 'flex',
                gap: '3px',
                alignItems: 'center'
              }} data-id="element-147">
                  {STEPS.map(s => <div key={s.id} style={{
                  width: s.id === step ? '16px' : '4px',
                  height: '4px',
                  borderRadius: '1px',
                  backgroundColor: completedSteps.has(s.id) ? c.green : s.id === step ? c.green : c.border,
                  transition: 'all 0.3s ease'
                }} data-id="element-148" />)}
                </div>
              </div>
              <h1 style={{
              margin: 0,
              fontSize: '40px',
              fontWeight: 800,
              color: c.white,
              letterSpacing: '-1.5px',
              lineHeight: 1.1
            }} data-id="element-149">
                {stepTitles[step].title}
              </h1>
              <p style={{
              margin: '6px 0 0',
              fontSize: '14px',
              color: c.textSecondary,
              letterSpacing: '0.1px'
            }} data-id="element-150">
                {stepTitles[step].subtitle}
              </p>
            </div>
          </div>

          {/* Scrollable form body */}
          <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '48px 56px'
        }} data-id="element-151">
            <div style={{
            maxWidth: '860px',
            margin: '0 auto',
            width: '100%'
          }} data-id="element-152">
              {step === 1 && <StepConditions conditions={conditions} allUploaded={allUploaded} uploadedCount={uploadedRequired.length} requiredCount={requiredConditions.length} data-id="element-153" />}
              {step === 2 && <StepBorrower form={form} set={setField} data-id="element-154" />}
              {step === 3 && <StepProperty form={form} set={setField} data-id="element-155" />}
              {step === 4 && <StepLoanTerms form={form} set={setField} data-id="element-156" />}
              {step === 5 && <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '40px'
            }} data-id="element-157">
                  <StepTransaction form={form} set={setField} data-id="element-158" />
                  <div style={{
                height: '1px',
                backgroundColor: c.border
              }} data-id="element-159" />
                  <StepLender form={form} set={setField} data-id="element-160" />
                </div>}
            </div>
          </div>

          {/* Bottom Navigation */}
          <div style={{
          padding: '18px 56px',
          borderTop: `1px solid ${c.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
          backgroundColor: c.sidebarBg,
          transition: 'background-color 0.2s'
        }} data-id="element-161">
            <div style={{
            maxWidth: '860px',
            margin: '0 auto',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }} data-id="element-162">
              <button onClick={handleBack} disabled={step === 1} style={{
              padding: '11px 24px',
              borderRadius: '10px',
              border: `1px solid ${c.border}`,
              backgroundColor: 'transparent',
              color: step === 1 ? c.textMuted : c.textSecondary,
              fontSize: '14px',
              fontWeight: 500,
              cursor: step === 1 ? 'default' : 'pointer',
              opacity: step === 1 ? 0.4 : 1,
              transition: 'all 0.15s'
            }} data-id="element-163">
                ← Back
              </button>

              <span style={{
              fontSize: '12px',
              color: c.textMuted
            }} data-id="element-164">
                {STEPS[step - 1].label}
              </span>

              {step < 5 ? <button onClick={step === 1 && !allUploaded ? undefined : handleNext} disabled={step === 1 && !allUploaded} title={step === 1 && !allUploaded ? 'All documents must be uploaded before proceeding' : undefined} style={{
              padding: '11px 28px',
              borderRadius: '10px',
              border: step === 1 && !allUploaded ? `1px solid ${c.border}` : 'none',
              backgroundColor: step === 1 && !allUploaded ? c.deepCard : c.green,
              color: step === 1 && !allUploaded ? c.textMuted : isDark ? '#0a0a0a' : '#ffffff',
              fontSize: '14px',
              fontWeight: 700,
              cursor: step === 1 && !allUploaded ? 'not-allowed' : 'pointer',
              letterSpacing: '0.3px',
              transition: 'all 0.15s',
              boxShadow: step === 1 && !allUploaded ? 'none' : `0 2px 12px ${c.green}40`
            }} data-id="element-165">
                  {step === 1 && !allUploaded ? 'Docs Required' : 'Continue →'}
                </button> : <button onClick={allUploaded ? handleSubmit : undefined} disabled={!allUploaded} title={!allUploaded ? 'All documents must be uploaded before sending to underwriting' : undefined} style={{
              padding: '11px 32px',
              borderRadius: '10px',
              border: allUploaded ? 'none' : `1px solid ${c.border}`,
              backgroundColor: allUploaded ? c.green : c.deepCard,
              color: allUploaded ? isDark ? '#0a0a0a' : '#ffffff' : c.textMuted,
              fontSize: '14px',
              fontWeight: 700,
              cursor: allUploaded ? 'pointer' : 'not-allowed',
              letterSpacing: '0.3px',
              transition: 'all 0.2s',
              boxShadow: allUploaded ? `0 2px 12px ${c.green}40` : 'none',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }} data-id="element-166">
                  {allUploaded ? '🚀 Send to Underwriting' : '🔒 Documents Required'}
                </button>}
            </div>
          </div>

          {submitted && <div style={{
          padding: '14px 56px',
          backgroundColor: c.greenFaint,
          borderTop: `1px solid ${c.green}33`,
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }} data-id="element-167">
              <span style={{
            fontSize: '16px'
          }} data-id="element-168">
                ✅
              </span>
              <div data-id="element-169">
                <p style={{
              fontSize: '12px',
              fontWeight: 700,
              color: c.green,
              margin: 0,
              letterSpacing: '0.5px'
            }} data-id="element-170">
                  SUBMISSION SUCCESSFUL
                </p>
                <p style={{
              fontSize: '12px',
              color: c.textSecondary,
              margin: '2px 0 0'
            }} data-id="element-171">
                  Loan packet handed off to submission specialist and queued for
                  underwriting.
                </p>
              </div>
            </div>}
        </main>
      </div>
    </ThemeContext.Provider>;
}